import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useAutomations from "../../../hooks/automationhooks/useAutomations";

const COLOR_SURFACE = "#2C2B2D";
const COLOR_HEADER = "#1E1E1E";
const COLOR_ACCENT = "#0DA3E2";

export default function SavedQuerySelectionModal({
  open,
  onClose,
  brandId,
  onUse,
  onEdit,
  onCreateNew,
}) {
  const {
    handleGetSavedQueries,
    handleDeleteSavedQuery,
  } = useAutomations();
  const [queries, setQueries] = useState([]);
  const [search, setSearch] = useState("");
  const [isFetchingQueries, setIsFetchingQueries] = useState(false);
  const hasInitialLoadCompletedRef = useRef(false);
  const latestRequestIdRef = useRef(0);
  const handleGetSavedQueriesRef = useRef(handleGetSavedQueries);
  const loaderDelayTimerRef = useRef(null);

  useEffect(() => {
    handleGetSavedQueriesRef.current = handleGetSavedQueries;
  }, [handleGetSavedQueries]);

  const showLoaderWithDelay = () => {
    if (loaderDelayTimerRef.current) {
      clearTimeout(loaderDelayTimerRef.current);
    }
    loaderDelayTimerRef.current = setTimeout(() => {
      setIsFetchingQueries(true);
    }, 180);
  };

  const hideLoader = () => {
    if (loaderDelayTimerRef.current) {
      clearTimeout(loaderDelayTimerRef.current);
      loaderDelayTimerRef.current = null;
    }
    setIsFetchingQueries(false);
  };

  useEffect(() => {
    if (!open || !brandId) return;
    let cancelled = false;
    const requestId = ++latestRequestIdRef.current;
    setSearch("");
    hasInitialLoadCompletedRef.current = false;
    showLoaderWithDelay();

    (async () => {
      try {
        const list = await handleGetSavedQueriesRef.current(brandId, "");
        if (cancelled || requestId !== latestRequestIdRef.current) return;
        setQueries(list || []);
      } finally {
        if (cancelled || requestId !== latestRequestIdRef.current) return;
        hideLoader();
        hasInitialLoadCompletedRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, brandId]);

  useEffect(() => {
    if (!open || !brandId || !hasInitialLoadCompletedRef.current) return;
    if (search.trim() === "") return;
    const timeoutId = setTimeout(() => {
      const requestId = ++latestRequestIdRef.current;

      (async () => {
        const list = await handleGetSavedQueriesRef.current(brandId, search);
        if (requestId !== latestRequestIdRef.current) return;
        setQueries(list || []);
      })();
    }, 250);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [search, open, brandId]);

  useEffect(() => {
    return () => {
      if (loaderDelayTimerRef.current) {
        clearTimeout(loaderDelayTimerRef.current);
      }
    };
  }, []);

  const visibleQueries = useMemo(() => {
    if (!search.trim()) return queries;
    const term = search.toLowerCase().trim();
    return queries.filter((q) => q.Name?.toLowerCase().includes(term));
  }, [queries, search]);

  const handleDelete = async (query) => {
    const shouldDelete = window.confirm(
      `Delete saved query "${query.Name}"? This cannot be undone.`
    );
    if (!shouldDelete) return;
    try {
      await handleDeleteSavedQuery(query.Id, brandId);
      const requestId = ++latestRequestIdRef.current;
      const list = await handleGetSavedQueriesRef.current(brandId, search);
      if (requestId === latestRequestIdRef.current) {
        setQueries(list || []);
      }
    } catch (error) {
      console.error("Failed to delete saved query:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "980px",
          maxWidth: "95vw",
          height: "680px",
          maxHeight: "90vh",
          bgcolor: COLOR_SURFACE,
          color: "#fff",
          border: "1px solid rgba(173, 180, 189, 0.45)",
          borderRadius: "3px",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: COLOR_HEADER,
          color: COLOR_ACCENT,
          fontWeight: 700,
          borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
        }}
      >
        Use Saved Query
      </DialogTitle>
      <DialogContent
        sx={{
          pt: "20px !important",
          pb: "12px !important",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          bgcolor: COLOR_SURFACE,
          color: "#FFFFFF",
        }}
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search saved queries..."
          size="small"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              bgcolor: COLOR_HEADER,
              "& fieldset": {
                borderColor: "rgba(173, 180, 189, 0.35)",
              },
            },
          }}
        />

        {isFetchingQueries ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
              minHeight: 220,
            }}
          >
            <CircularProgress size={60} sx={{ color: COLOR_ACCENT }} />
            <Typography sx={{ color: "#FFFFFF" }}>Loading...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {visibleQueries.map((query) => (
              <Box
                key={query.Id}
                sx={{
                  border: "1px solid rgba(173, 180, 189, 0.35)",
                  borderRadius: "8px",
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: COLOR_ACCENT, fontWeight: 600 }}>
                    {query.Name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.75)" }}
                  >
                    {query.Description || "No description"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Button
                    variant="outlined"
                    onClick={() => onUse?.(query)}
                    sx={{
                      borderColor: COLOR_ACCENT,
                      color: "#fff",
                      textTransform: "none",
                      minWidth: "72px",
                      "&:hover": {
                        borderColor: COLOR_ACCENT,
                        bgcolor: "rgba(13, 163, 226, 0.08)",
                      },
                    }}
                  >
                    Use
                  </Button>
                  <Tooltip title="Edit" placement="top">
                    <IconButton onClick={() => onEdit?.(query)} sx={{ color: "#fff" }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      onClick={() => handleDelete(query)}
                      sx={{ color: "#fff" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}

            {visibleQueries.length === 0 && (
              <Typography
                sx={{ color: "rgba(255,255,255,0.7)", textAlign: "center", py: 4 }}
              >
                No saved queries found.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          justifyContent: "space-between",
          bgcolor: COLOR_SURFACE,
        }}
      >
        <Button
          variant="outlined"
          onClick={onCreateNew}
          sx={{
            width: "187px",
            height: "50px",
            minWidth: "187px",
            borderRadius: "7px",
            borderColor: COLOR_ACCENT,
            color: "#FFFFFF",
            textTransform: "none",
            "&:hover": {
              borderColor: COLOR_ACCENT,
              bgcolor: "rgba(13, 163, 226, 0.08)",
            },
          }}
        >
          Create New Query
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            width: "187px",
            height: "50px",
            minWidth: "187px",
            borderRadius: "7px",
            borderColor: COLOR_ACCENT,
            color: "#fff",
            textTransform: "none",
            "&:hover": {
              borderColor: COLOR_ACCENT,
              bgcolor: "rgba(13, 163, 226, 0.08)",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
